import { CURRENT_YEAR, START_YEAR_PRODUCTION } from "@shared/config/Date";
import { PostFormState } from "./postFormSlice.types";
import { checkIsFilled } from "./utils/checkIsFilled";

export type Validator<T> = (value: T) => ValidationResult;
export interface LoadDefaultValuesPayload {
    brand?: string;
    brandUuid?: string;
    model?: string;
    modelUuid?: string;
    generationUuid?: string;
    generationName?: string;
    year?: number;
    body?: string;
    engine?: string;
    drive?: string;
    transmission?: string;
    wheel?: string;
    horsePower?: number;
    color?: string;
    mileage?: number;
    vin?: string;
    description?: string;
    ownersQnt?: number;
    sellerType?: string;
    contact?: string;
    phone?: string;
    price?: number;
    city?: string;
    country?: string;
    address?: string;
    pics: PicData[];
}

export interface PicData {
    bigUrl?: string;
    url: string;
}

export interface ValidationResult {
    isValid: boolean;
    errorMessage: string;
}

export interface ValidatorsMap {
    year: Validator<number>;
    price: Validator<number>;
    phone: Validator<string>;
    name: Validator<string>;
    description: Validator<string>;
    color: Validator<string>;
    horsePower: Validator<number>;
    vin: Validator<string>;
    mileage: Validator<number>;
    ownersQnt: Validator<number>;
    stringRequired: Validator<string>;
    bodyType: Validator<string>;
    transmission: Validator<string>;
    wheel: Validator<string>;
    engine: Validator<string>;
    drive: Validator<string>;
}

export const createValidatedSetter =
    <T>(fieldName: keyof PostFormState, validatorName: keyof ValidatorsMap) =>
    (state: PostFormState, action: { payload: T }) => {
        const value = action.payload;
        const validator = validators[validatorName] as Validator<T>;
        const validation = validator ? validator(value) : { isValid: true, errorMessage: "" };

        (state as unknown as Record<string, unknown>)[fieldName as string] = value;

        const updatedField = `${String(fieldName)}Updated` as keyof PostFormState;
        const validField = `${String(fieldName)}Valid` as keyof PostFormState;
        const errorField = `${String(fieldName)}ErrMessage` as keyof PostFormState;

        if (updatedField in state) {
            (state as unknown as Record<string, unknown>)[updatedField as string] = true;
        }
        if (validField in state) {
            (state as unknown as Record<string, unknown>)[validField as string] =
                validation.isValid;
        }

        if (errorField in state) {
            (state as unknown as Record<string, unknown>)[errorField as string] =
                validation.errorMessage;
        }

        state.isFilled = checkIsFilled(state);
    };

export const createSimpleSetter =
    <T>(fieldName: keyof PostFormState) =>
    (state: PostFormState, action: { payload: T }) => {
        (state as unknown as Record<string, unknown>)[fieldName as string] = action.payload;
        state.isFilled = checkIsFilled(state);
    };

export const validators: ValidatorsMap = {
    year: (value: number): ValidationResult => {
        if (!value || value.toString().length !== 4) {
            return { isValid: false, errorMessage: "Неверный формат года" };
        }
        if (value < START_YEAR_PRODUCTION || value > CURRENT_YEAR) {
            return { isValid: false, errorMessage: "Некорректное значение" };
        }
        return { isValid: true, errorMessage: "" };
    },

    price: (value: number): ValidationResult => {
        if (!value || value.toString().length === 0) {
            return { isValid: false, errorMessage: "Не заполнено" };
        }
        if (value < 100 || value > 500000000) {
            return { isValid: false, errorMessage: "Некорректное значение" };
        }
        return { isValid: true, errorMessage: "" };
    },

    phone: (value: string): ValidationResult => {
        if (!value || value.length === 0) {
            return { isValid: false, errorMessage: "Не заполнено" };
        }
        if (value.length <= 10) {
            return { isValid: false, errorMessage: "Не заполнено полностью" };
        }
        return { isValid: true, errorMessage: "" };
    },

    name: (value: string): ValidationResult => {
        const regexp = /^[a-zA-Zа-яА-Я-]+$/;
        if (!regexp.test(value)) {
            return { isValid: false, errorMessage: "Недопустимые символы в имени" };
        }
        if (value.length === 0) {
            return { isValid: false, errorMessage: "Не заполнено" };
        }
        return { isValid: true, errorMessage: "" };
    },

    description: (value: string): ValidationResult => {
        if (!value || value.length === 0) {
            return { isValid: false, errorMessage: "Не заполнено" };
        }
        return { isValid: true, errorMessage: "" };
    },

    color: (value: string): ValidationResult => {
        if (!value || value.length === 0) {
            return { isValid: false, errorMessage: "" };
        }
        return { isValid: true, errorMessage: "" };
    },

    horsePower: (value: number): ValidationResult => {
        if (!value || value.toString().length === 0) {
            return { isValid: false, errorMessage: "не заполнено" };
        }
        if (value < 5 || value > 1000) {
            return { isValid: false, errorMessage: "неверное значение" };
        }
        return { isValid: true, errorMessage: "" };
    },

    vin: (value: string): ValidationResult => {
        if (!value || value.toString().length === 0) {
            return { isValid: false, errorMessage: "не заполнен" };
        }
        if (value.toString().length !== 17) {
            return { isValid: false, errorMessage: "номер должен состоять из 17 символов" };
        }
        return { isValid: true, errorMessage: "" };
    },

    mileage: (value: number): ValidationResult => {
        if (!value || value <= 0) {
            return { isValid: false, errorMessage: "" };
        }
        return { isValid: true, errorMessage: "" };
    },

    ownersQnt: (value: number): ValidationResult => {
        if (!value || value <= 0) {
            return { isValid: false, errorMessage: "Не заполнено" };
        }
        return { isValid: true, errorMessage: "" };
    },

    stringRequired: (value: string): ValidationResult => {
        if (!value || value.toString().length === 0) {
            return { isValid: false, errorMessage: "" };
        }
        return { isValid: true, errorMessage: "" };
    },

    bodyType: (value: string): ValidationResult => {
        if (!value || value.toString().length === 0) {
            return { isValid: false, errorMessage: "" };
        }
        return { isValid: true, errorMessage: "" };
    },

    transmission: (value: string): ValidationResult => {
        if (!value || value.toString().length === 0) {
            return { isValid: false, errorMessage: "" };
        }
        return { isValid: true, errorMessage: "" };
    },

    wheel: (value: string): ValidationResult => {
        if (!value || value.toString().length === 0) {
            return { isValid: false, errorMessage: "" };
        }
        return { isValid: true, errorMessage: "" };
    },

    engine: (value: string): ValidationResult => {
        return { isValid: Boolean(value), errorMessage: "" };
    },

    drive: (value: string): ValidationResult => {
        return { isValid: Boolean(value), errorMessage: "" };
    }
};